/**
 * Legale **Respawns** — eine geschlagene Figur wieder einsetzen (§22).
 *
 * Geschlagene Figuren sind nicht dauerhaft verloren. Statt einer anderen Aktion darf ein
 * Spieler eine aus seinem Pool zurück aufs Brett bringen; das verbraucht den **vollständigen
 * Zug**. Erlaubt ist nur ein **freies Feld der eigenen Startzone** — ist die Startzone
 * vollständig besetzt, ist Respawn nicht möglich.
 *
 * Der **Fähnrich** respawnt nach demselben Verfahren, ohne Sonderbehandlung (vom User am
 * 2026-10-02 bestätigt, vorher `OPEN_QUESTIONS.md` Nr. 4). Daraus folgt unmittelbar: ohne
 * Fähnrich auf dem Brett kann ein Spieler nicht punkten, und bei vollständig besetzter
 * Startzone kommt er bis auf Weiteres nicht zurück.
 *
 * Respawn ist über die RuleConfig abschaltbar (`respawn.enabled`, §52).
 *
 * Welcher Sektor einem Spieler gehört, steht im `RuleContext` — die Zuweisung trifft der
 * Host bei der Partieerstellung, die Engine leitet sie nicht her.
 */
import { capturedOf } from './board-state.js';
import { hexKey } from './hex.js';
import { freeStartZoneCells } from './rule-context.js';
/** Die Figuren, die dieser Spieler wieder einsetzen könnte. Leer, wenn Respawn aus ist. */
export function respawnablePieces(context, player) {
    if (!context.config.respawn.enabled)
        return [];
    return capturedOf(context.state, player);
}
/**
 * Die freien Felder der eigenen Startzone.
 *
 * Für die Oberfläche: §22 beschreibt die Auswahl als „Figur wählen → freies Feld wählen",
 * und dafür braucht das UI die Felder einmal, nicht einmal pro Figur.
 */
export function respawnCells(context, player) {
    // Der Schalter ist der **einzige** Unterschied zur Aufstellung (§10): die findet immer statt,
    // der Respawn ist abschaltbar. Die Felder selbst sind dieselbe Menge, also eine Definition
    // (`rule-context.ts`, `freeStartZoneCells`).
    if (!context.config.respawn.enabled)
        return [];
    return freeStartZoneCells(context, player);
}
/**
 * Jede Kombination aus wiedereinsetzbarer Figur und freiem Startfeld.
 *
 * Das Kreuzprodukt ist beabsichtigt: „welche Figur auf welches Feld" ist **eine**
 * Entscheidung und braucht deshalb **eine** Action ID (§74). Für eine
 * Zwei-Schritt-Auswahl im UI gibt es `respawnablePieces` und `respawnCells`.
 */
export function getLegalRespawns(context, player) {
    const pieces = respawnablePieces(context, player);
    if (pieces.length === 0)
        return [];
    const cells = respawnCells(context, player);
    const respawns = [];
    for (const piece of pieces) {
        for (const cell of cells) {
            respawns.push({ piece: piece.id, to: cell });
        }
    }
    return respawns;
}
/** Hilfsprädikat für die Validierung einer eingehenden Respawn-Absicht. */
export function isLegalRespawn(context, player, pieceId, to) {
    const target = hexKey(to);
    return getLegalRespawns(context, player).some((entry) => entry.piece === pieceId && hexKey(entry.to) === target);
}
//# sourceMappingURL=legal-respawns.js.map